import { useCallback } from 'react'

import {
  addressBookOnchainReceive,
  addressBookOnchainSend,
} from '@services/sections/lib/address-book'
import { useSegment, SegmentEvents } from '@services/segment'
import { ComplexTable, TableCard } from '@shared/components/tables'
import { TransactionType, usePaymentsQuery } from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { useAddressTransactionsTableColumns } from './AddressTransactionsTable.Columns'

import type { Row } from '@circlefin/components/lib/AdvancedTable'
import type { Payment } from '@shared/graphql'

const { Header, Title, Body } = TableCard

export interface AddressTransactionsTableProps {
  /*
   * Recipient Address ID.
   */
  addressId: string
}

export const AddressTransactionsTable: React.FC<
  AddressTransactionsTableProps
> = ({ addressId }) => {
  const router = useRouter()
  const { track } = useSegment()
  const { t } = useTranslation('address-book/containers')
  const columns = useAddressTransactionsTableColumns()

  const { data, loading, error, refetch } = usePaymentsQuery({
    variables: {
      page: {
        pageSize: 10,
      },
      filter: {
        type: [TransactionType.onchain_send, TransactionType.onchain_receive],
        refId: addressId,
      },
    },
  })

  const onRowClick = useCallback(
    ({ original: { id, type } }: Row<Payment>) => {
      if (type === TransactionType.onchain_send) {
        void router.push({
          pathname: addressBookOnchainSend.route,
          query: { id },
        })
      } else {
        void router.push({
          pathname: addressBookOnchainReceive.route,
          query: { id },
        })
      }

      track(SegmentEvents.TransactionDetailsClicked, {
        props: {
          transaction_type: type,
        },
      })
    },
    [router, track],
  )

  return (
    <TableCard>
      <Header>
        <Title>{t`transactions-table.title`}</Title>
      </Header>
      <Body>
        <ComplexTable<Payment>
          columns={columns}
          data={data?.payments}
          empty={{ subtitle: t`transactions-table.emptyTableLabel` }}
          enableSorting={false}
          error={error}
          loading={loading}
          onRowClick={onRowClick}
          retry={refetch}
        />
      </Body>
    </TableCard>
  )
}
